import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { LocalStorage } from '@tecommon/ui/components/localStorage/localStorage.service'
import { MatDialog } from '@angular/material/dialog';
import { Interrupt, InterruptArgs, InterruptSource, DEFAULT_INTERRUPTSOURCES } from './interrupts';
import { TranslateService } from '@ngx-translate/core';
/**
 * A service for detecting and responding to user idleness and session timeout.
 */
@Injectable()
export class SessionCheck implements OnDestroy
{
    private idle: number;
    private timeoutVal: number;
    private running = false;
    private idling: boolean;
    private idleHandle: any;  // assigned the watch function to handle checking idleness.
    private timeoutHandle: any;  // assigned the timeout doCountdown function to handle the countdown before timeout() is called.
    private countdown: number;
    private dialogOpen: boolean = false;
    private interrupts: Array<Interrupt> = new Array;

    public onIdleStart: EventEmitter<any> = new EventEmitter;
    public onIdleEnd: EventEmitter<any> = new EventEmitter;
    public onTimeoutWarning: EventEmitter<number> = new EventEmitter<number>();
    public onTimeout: EventEmitter<number> = new EventEmitter<number>();
    public onInterrupt: EventEmitter<any> = new EventEmitter;

    //[key: string]: any;

    constructor(private localStorage: LocalStorage, private readonly dialog: MatDialog,
        private translate: TranslateService) {
        //debugger;
        this.idle = 3 * 60 - 30;  // 3 minutes
        // sets a timeout period of 3 minutes.  Then you have a 30 second timeout window where we warn the user and give them a chance to restart the idle timer.
        this.timeoutVal = 30;  // 30 seconds

        this.setIdling(false);

        // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
        this.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        // idle.setAutoResume(AutoResume.notIdle)  // I did this by removing config options, it's always set to AutoResume.  -TomC

        this.onTimeoutWarning.subscribe(() =>
        {
            this.openDialog();
            //console.log(`You will time out in ${countdown} seconds!`);
        });

        this.onInterrupt.subscribe(() => this.pushExpirationCookie());
        //this.onIdleStart.subscribe(() => console.log('You\'ve gone idle.'));
        //this.onIdleEnd.subscribe(() => console.log('No longer idle.'));
    }

    pushExpirationCookie(): void {
        const date = new Date();
        //debugger;
        date.setTime(date.getTime() + (180 * 1000));
        // expires in 60 seconds.  The server will send a refreshed cookie if they are just refreshing
        // the page.  If they're closing the browser, they'll be logged out shortly..  I hope.

        const cookieName = 'myID.PeanutButterChocolateChip';
        let cookieValue = '';
        const nameEQ = encodeURIComponent(cookieName) + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++)
        {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0)
            {
                cookieValue = c.substring(nameEQ.length, c.length);  //decodeURIComponent(
            }
        }
        document.cookie = `${cookieName}=${cookieValue}; Path=/; Expires=${date.toUTCString()}`; 
    }

    setInterrupts(sources: Array<InterruptSource>): Array<Interrupt>
    {
        this.clearInterrupts();

        let self = this;

        //console.log("Setting interrupts: ");
        //console.log(sources);
        for (let source of sources)
        {
            let sub = new Interrupt(source);
            sub.subscribe((args: InterruptArgs) =>
            {
                self.interrupt(args.force, args.innerArgs);
            });

            this.interrupts.push(sub);
        }

        return this.interrupts;
    }

    /*
     * Signals that user activity has occurred.  The interrupt class has emitted an event that we subscribed to
     * in "setInterrupts()" above that tells us the user did something on our site.  (probably a mouseover.. ugh)
     * So.. do we consider that good enough to keep a session alive with the server (and ping the server to keep that
     * session alive for them..) or do we ignore this interaction and only care about button presses or http events?  -TomC
     * 
     * @param force - Forces watch to be called, unless they are timed out.
     * @param eventArgs - Optional source event arguments.
     */
    interrupt(force?: boolean, eventArgs?: any): void
    {
        if (!this.running)
        {
            //console.log("Not running");
            return;
        }

        if (this.isExpired())
        {
            //debugger;
            //console.log("Timed out, too late");
            this.timeout();
            return;
        }

        //console.log("Interrupt occurred!");
        //console.log(eventArgs);
        this.onInterrupt.emit(eventArgs);

        // we automatically resume watching after an interrupt occurs, no need for different config options or "force" parameter.
        this.watch();
    }

    private toggleInterrupts(resume: boolean): void
    {
        for (let interrupt of this.interrupts)
        {
            if (resume)
            {
                interrupt.resume();
            } else
            {
                interrupt.pause();
            }
        }
    }

    /*
     * Pauses, unsubscribes, and clears the current interrupt subscriptions.
     */
    clearInterrupts(): void
    {
        for (let sub of this.interrupts)
        {
            sub.pause();
            sub.unsubscribe();
        }

        this.interrupts.length = 0;
    }


    /*
     * Returns the current idle value.
     * @return The idle value in seconds.
     */
    getIdle(): number
    {
        return this.idle;
    }

    /*
     * Returns whether or not the user is considered idle.
     * @return True if the user is in the idle state; otherwise, false.
     */
    isIdling(): boolean
    {
        return this.idling;
    }

    /*
     * Starts watching for inactivity.
     */
    watch(): void
    {
        //debugger;
        //console.log("WATCH!!!");
        this.safeClearInterval('idleHandle');
        this.safeClearInterval('timeoutHandle');

        const timeout = !this.timeoutVal ? 0 : this.timeoutVal;
        const value = new Date(this.now().getTime() + ((this.idle + timeout) * 1000));
        this.last(value);

        if (this.idling)
        {
            this.toggleState();
        }
        if (!this.running)
        {
            this.toggleInterrupts(true);
        }

        this.running = true;

        const watchFn = () =>
        {
            const diff = this.getExpiryDiff(timeout);
            if (diff > 0)
            {
                this.safeClearInterval('idleHandle');
                this.idleHandle = setInterval(watchFn, diff);
            } else
            {
                this.toggleState();
            }
        };

        this.idleHandle = setInterval(watchFn, this.idle * 1000);
    }

    /*
     * Forces a timeout event and state.
     */
    timeout(): void
    {
        //debugger;
        this.toggleInterrupts(false);
        this.safeClearInterval('idleHandle');
        this.safeClearInterval('timeoutHandle');

        this.setIdling(true);
        this.running = false;
        this.countdown = 0;

        this.onTimeout.emit(null);
    }

    private toggleState(): void
    {
        //console.log("Toggle State");
        this.setIdling(!this.idling);

        if (this.idling)
        {
            this.onIdleStart.emit(null);

            if (this.timeoutVal > 0)
            {
                this.countdown = this.timeoutVal;
                this.doCountdown();
                this.timeoutHandle = setInterval(() =>
                {
                    this.doCountdown();
                }, 1000);
            }
        } else
        {
            this.toggleInterrupts(true);
            this.onIdleEnd.emit(null);
        }

        this.safeClearInterval('idleHandle');
    }

    private getExpiryDiff(timeout: number): number
    {
        const now = this.now();
        const last = this.last() || now;
        return last.getTime() - now.getTime() - (timeout * 1000);
    }

    private doCountdown(): void
    {
        const timeout = !this.timeoutVal ? 0 : this.timeoutVal;
        const diff = this.getExpiryDiff(timeout);
        if (diff > 0)
        {
            this.safeClearInterval('timeoutHandle');
            this.interrupt(true);
            return;
        }

        if (!this.idling)
        {
            return;
        }

        if (this.countdown <= 0)
        {
            this.timeout();
            return;
        }

        this.onTimeoutWarning.emit(this.countdown);
        this.countdown--;
    }

    private safeClearInterval(handleName: string): void
    {
        const handle = this[handleName];
        if (handle !== null && typeof handle !== 'undefined')
        {
            clearInterval(this[handleName]);
            this[handleName] = null;
        }
    }

    private getExpiry(): Date
    {
        const expiry: string = this.localStorage.getItem('Expire');
        if (expiry)
        {
            return new Date(parseInt(expiry, 10));  // base 10 :)
        } else
        {
            return null;
        }
    }

    private setExpiry(value: Date) {
        if (value)
        {
            this.localStorage.setItem('Expire', value.getTime().toString());
            //console.log("Set Expiry: " + value.toString());
        } else
        {
            this.localStorage.removeItem( 'Expire');
        }
    }

    private setIdling(value: boolean)
    {
        this.idling = value;
        this.localStorage.setItem('Idling', value.toString());
    }

    private last(value?: Date): Date
    {
        if (value !== void 0)
        {
            this.setExpiry(value);
        }
        return this.getExpiry();
    }

    private isExpired(): boolean
    {
        const expiry = this.last();
        return expiry != null && expiry <= this.now();
    }

    private now(): Date
    {
        return new Date();
    }

    /*
     * Called by Angular when destroying the instance.
     */
    ngOnDestroy(): void
    {
        console.log("ngOnDestroy");
        this.toggleInterrupts(false);
        this.clearInterrupts();
        this.safeClearInterval('idleHandle');
        this.safeClearInterval('timeoutHandle');

        this.setIdling(false);
        this.running = false;

        this.last(null);
    }

    private openDialog(): void {
        if (!this.dialogOpen) {
            this.toggleInterrupts(false);
            var dialog = this.dialog.open(SessionTimeoutDialog,
                {
                    disableClose: true,
                    closeOnNavigation: false,
                    width: "350px",
                    panelClass: "te-col-center",
                    backdropClass: "sessionExpiredBackdrop"
                });
            this.dialogOpen = true;
            dialog.afterClosed().subscribe(() => this.dialogOpen = false);
        }
    }
}

import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';

@Component({
    selector: 'sessionTimeoutDialog',
    templateUrl: 'sessionTimeoutDialog.component.html',
    encapsulation: ViewEncapsulation.None,
    styles: [`
.sessionExpiredBackdrop {
    background: #545658;
    opacity: .288 !important;
}
.sessionExpiredBackdrop.cdk-overlay-backdrop-showing {
    transition-property: opacity;
    transition-duration: 10s;
    transition-timing-function: ease-in;
    opacity: 100 !important;
}
`],
    standalone: false
})
export class SessionTimeoutDialog
{
    idleState: string;
    timedOut = false;
    constructor(public dialogRef: MatDialogRef<SessionTimeoutDialog>, @Inject(MAT_DIALOG_DATA) public data: any,
        private readonly http: HttpClient, private idle: SessionCheck) //Idle
    {
        this.idleState = " 30 ";
        idle.onTimeoutWarning.subscribe((countdown) => this.idleState = ` ${countdown} `);
        idle.onTimeout.subscribe(() =>
        {
            this.timedOut = true;
        });
    }

    public onReloadClick(): void
    {
        this.idle.ngOnDestroy();  // clean up after yourself!
        window.location.replace("/Login");
        document.location.reload();
    }

    public onStaySignedInClick(): void {
        // do something to ping server here.
        this.http.get('/api/site/keepalive', { responseType: 'text' }).subscribe(result => {
        },
            error => {
                console.log(error.error);
                this.idle.timeout();
            }
        );
        // Interrupt the expiry countdown and then start watching again.
        this.idle.interrupt();
        this.idle.onInterrupt.subscribe(() => this.idle.pushExpirationCookie());
        // then close the dialog since they interacted with the site in time!
        this.dialogRef.close();
    }
}